import { useMemo, useState } from 'react';

import type { Props } from '..';
import type { CarouselCallbackProps } from '../../../carousels/EmblaCarousel';

export type UseMediaLightBox = <T>(
  /**
   * The array of media items.
   */
  mediaArr: T[],
  /**
   * A callback function to transform the media items into the required format.
   */
  transformer?: (transformer: T[]) => Props['media'],
) => Props;

/**
 * Custom hook for a media light box
 *
 * @param mediaArr - The array of media items.
 * @param transformer - The function to transform the media items.
 * @returns An object containing the state and functions for the media light box.
 */
export const useMediaLightBox: UseMediaLightBox = (mediaArr, transformer) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startIndex, setStartIndex] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleOpen = (index = 0) => {
    if (startIndex === null) setStartIndex(index);
    setActiveIndex(index);
    setIsOpen(true);
  };

  const handleClose = () => {
    setStartIndex(null);
    setActiveIndex(0);
    setIsOpen(false);
  };

  const handleOnNavClick = ({ selectedIndex, totalSlides }: CarouselCallbackProps) => {
    setActiveIndex(selectedIndex + 1 > totalSlides ? 0 : selectedIndex);
  };

  const handleOnSwipe = ({ selectedIndex, totalSlides }: CarouselCallbackProps) => {
    setActiveIndex(selectedIndex + 1 > totalSlides ? 0 : selectedIndex);
  };

  const media = useMemo(
    () => (transformer ? transformer(mediaArr) : (mediaArr as unknown as Props['media'])),
    [transformer, mediaArr],
  );

  return {
    isOpen,
    startIndex,
    activeIndex,
    handleOpen,
    handleClose,
    media,
    handleOnNavClick,
    handleOnSwipe,
  };
};
